import type { TrafficCalculatorSliderRange } from "@/components/services/seo/seo-revenue-projection";

/** Slider ranges for "Vad är ett möte värt?" (defaults are labelled examples). */
export const MEETING_CALCULATOR_RANGES = {
  meetings: { min: 1, max: 40, step: 1, fallback: 8 },
  close: { min: 1, max: 60, step: 1, fallback: 20 },
  value: { min: 10_000, max: 2_000_000, step: 10_000, dragStep: 5_000, fallback: 120_000 },
  lifetime: { min: 1, max: 10, step: 1, fallback: 3 },
} satisfies Record<string, TrafficCalculatorSliderRange>;

/** Years shown in the revenue chart. */
export const MEETING_VALUE_YEARS = 5;

export type MeetingValueInputs = {
  /** Booked meetings per month. */
  meetings: number;
  /** Share of meetings that become customers, in percent (0–100). */
  closePercent: number;
  /** Average deal value per year, SEK. */
  value: number;
  /** Years a customer stays. */
  lifetime: number;
};

export type MeetingValueResult = {
  /** Revenue from one customer over its lifetime. */
  customerValue: number;
  /** Lifetime revenue per booked meeting (customer value × close rate). */
  meetingValue: number;
  /** New customers per year. */
  customersPerYear: number;
  /** Revenue in year 1 from customers won that year. */
  yearOneRevenue: number;
  /** Revenue per year 1–5: each year's new customers plus earlier ones still active. */
  years: number[];
};

/**
 * What a booked meeting is worth: customer value × close rate, and the
 * revenue a steady flow of meetings builds year by year (cohorts stay for
 * `lifetime` years). All inputs are clamped to sensible non-negative values.
 */
export function computeMeetingValue(inputs: MeetingValueInputs): MeetingValueResult {
  const meetings = Math.max(0, inputs.meetings);
  const close = Math.min(100, Math.max(0, inputs.closePercent)) / 100;
  const value = Math.max(0, inputs.value);
  const lifetime = Math.max(1, Math.round(inputs.lifetime));

  const customerValue = value * lifetime;
  const meetingValue = customerValue * close;
  const customersPerYear = meetings * 12 * close;
  const years = Array.from(
    { length: MEETING_VALUE_YEARS },
    (_, index) => customersPerYear * value * Math.min(index + 1, lifetime),
  );

  return {
    customerValue,
    meetingValue,
    customersPerYear,
    yearOneRevenue: years[0] ?? 0,
    years,
  };
}
